import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, InputRef, Modal, Select, Table, message, Tag } from 'antd';
import { purchaseOrder, purchaseOrderDetail, initPurchaseOrder } from '../../model/purchase-order';
import { initProduct, product, vendor } from '../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_product, api_product_lookup_code, api_good_receive, api_purchase_order } from '../../api/product';
import { api_vendor } from '../../api/vendor';
import { isEmpty } from '../../utils/util';
import { PageSizeSelect } from '../common/table-pagination';
import PurchaseOrderPrint, { printPurchaseOrderDocument } from './purchase-order-print';
import { PRODUCT_PAGE_SIZE_OPTIONS, PRODUCT_DEFAULT_PAGE_SIZE } from '../common/common-search';
import { useNavigate } from 'react-router-dom';
import { describeBlankLines, insertBlankLine, linesNeedingQuantity, needsPoReorder, needsQuantity, poSellableQuantity } from './po-lines';
import { backendMessage, isConfirmedWrite } from '../../utils/write-result';

const { Option } = Select;
const { TextArea } = Input;

interface Props {
    purchaseOrder: purchaseOrder;
    disabled?: boolean;
    products?: product[];
    vendors?: vendor[];
    onChange: (data: purchaseOrder) => void;
    onSaveSuccess?: () => void;
    onReload?: () => void;
}

const PurchaseOrderDetail: React.FC<Props> = (props) => {
    const navigate = useNavigate();
    const barcodeInputRef = useRef<InputRef>(null);

    const [data, setData] = useState<purchaseOrder>(() => props.purchaseOrder || initPurchaseOrder);
    const [vendorList, setVendorList] = useState<vendor[]>(() => props.vendors || []);
    const [allProducts, setAllProducts] = useState<product[]>(() => props.products || []);
    const [lowStockProducts, setLowStockProducts] = useState<product[]>([]);

    // Product search modal state
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [productSearchKeyword, setProductSearchKeyword] = useState('');
    const [barcodeInput, setBarcodeInput] = useState('');
    const [modalPageSize, setModalPageSize] = useState<number>(PRODUCT_DEFAULT_PAGE_SIZE);
    const [modalPage, setModalPage] = useState<number>(1);
    const [modalTotal, setModalTotal] = useState<number>(0);

    // Print Modal
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const printRef = useRef<HTMLDivElement>(null);

    const handlePrintPurchaseOrder = () => {
        printPurchaseOrderDocument(printRef.current);
    };

    useEffect(() => {
        if (props.products && props.products.length > 0) {
            setAllProducts(props.products);
        }
        if (props.vendors && props.vendors.length > 0) {
            setVendorList(props.vendors);
        }
        loadVendorsAndProducts();
    }, [props.products, props.vendors]);

    useEffect(() => {
        if (props.purchaseOrder) {
            setData(props.purchaseOrder);
        }
    }, [props.purchaseOrder]);

    const isVendorMatch = (productVendor: any, targetVendorId: number | string, productVendorName?: string, targetVendorName?: string): boolean => {
        if (!targetVendorId && !targetVendorName) return false;
        const targetIdStr = String(targetVendorId || '').trim();

        // 1. Direct name match if vendor names available
        if (targetVendorName && productVendorName && targetVendorName.trim() && productVendorName.trim() === targetVendorName.trim()) {
            return true;
        }

        if (!targetIdStr || targetIdStr === '0') return false;

        // 2. Array of items or IDs
        if (Array.isArray(productVendor)) {
            return productVendor.some((v: any) => {
                if (typeof v === 'object' && v !== null) {
                    return String(v.id || '').trim() === targetIdStr;
                }
                return String(v || '').trim() === targetIdStr;
            });
        }

        // 3. Object with id
        if (typeof productVendor === 'object' && productVendor !== null) {
            return String(productVendor.id || '').trim() === targetIdStr;
        }

        // 4. Numeric direct match
        if (typeof productVendor === 'number') {
            return String(productVendor) === targetIdStr;
        }

        // 5. String parsing (e.g. "19,", "1,2,19,", "19", "19, 20")
        if (typeof productVendor === 'string') {
            const parts = productVendor.split(',').map(s => s.trim()).filter(Boolean);
            return parts.includes(targetIdStr);
        }

        return false;
    };

    const calculateLowStock = (vendorId: number, vendorName: string | undefined, products: product[]) => {
        if (!vendorId || !products || products.length === 0) return [];
        return products.filter(p => {
            const vendorMatch = isVendorMatch(p.vendor, vendorId, p.vendor_name, vendorName);
            // The third copy of the low-stock rule, and the one that
            // treated BOTH "unset" and "0" as 10. A shop that set 0 meaning
            // "do not reorder until it is actually gone" had that ignored,
            // and this button pulled the product in as soon as stock hit
            // ten. It now follows the same states as every other screen:
            // reorder what is OUT, plus what has reached a level the shop
            // actually set.
            //
            // NEVER_STOCKED is the one state this screen reads differently
            // from the rest of the app. Most of the shop's real quantities
            // have not been entered yet, so a product the owner deliberately
            // set a reorder point on can still have no stock rows at all --
            // and excluding every NEVER_STOCKED product pulled only the one
            // item that happened to have history. It is eligible HERE only
            // when a reorder point was explicitly set (0 counts), so the
            // thousands of unfinished catalogue imports still stay out. The
            // global low-stock cards keep treating NEVER_STOCKED as its own
            // state; that rule is not changed.
            //
            // What is compared is now what the shop can physically SELL --
            // its own stock plus consigned goods -- not its own stock alone.
            // A product with nothing of the shop's own but twenty consigned
            // on the shelf is not something to reorder. See needsPoReorder.
            const needsReorder = needsPoReorder(p);
            return vendorMatch && needsReorder;
        });
    };

    // Recalculate low stock items whenever selected vendor changes
    const fetchVendorLowStock = async (vendorId: number, vendorName?: string) => {
        if (!vendorId) {
            setLowStockProducts([]);
            return;
        }
        try {
            const res = await api_product('GET_BY_VALUE', {}, vendorId);
            const vList = Array.isArray(res) ? res : (res?.items || []);
            if (Array.isArray(vList) && vList.length > 0) {
                const low = calculateLowStock(vendorId, vendorName, vList);
                setLowStockProducts(low);
                return;
            }
            const fallbackLow = calculateLowStock(vendorId, vendorName, allProducts);
            setLowStockProducts(fallbackLow);
        } catch (e) {
            const fallbackLow = calculateLowStock(vendorId, vendorName, allProducts);
            setLowStockProducts(fallbackLow);
        }
    };

    useEffect(() => {
        if (data.vendor) {
            fetchVendorLowStock(data.vendor, data.vendor_name);
        } else {
            setLowStockProducts([]);
        }
    }, [data.vendor, data.vendor_name]);

    const loadVendorsAndProducts = async () => {
        try {
            const [vendorsRes, productsRes] = await Promise.all([
                api_vendor('LIST', {}),
                api_product('LIST', {})
            ]);
            if (Array.isArray(vendorsRes)) setVendorList(vendorsRes);
            const pList = Array.isArray(productsRes) ? productsRes : (productsRes?.items || []);
            if (Array.isArray(pList)) {
                setAllProducts(pList);
                const currentVendorId = props.purchaseOrder?.vendor || data.vendor;
                const currentVendorName = props.purchaseOrder?.vendor_name || data.vendor_name;
                if (currentVendorId) {
                    fetchVendorLowStock(currentVendorId, currentVendorName);
                }
            }
        } catch (e) {
            // fallback if mock
        }
    };

    const updateAndNotify = (updated: purchaseOrder) => {
        // Calculate totals
        const qty = updated.detail?.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0) || 0;
        const weight = updated.detail?.reduce((sum, item) => sum + ((Number(item.weight) || 0) * (Number(item.quantity) || 0)), 0) || 0;
        const subtotal = updated.detail?.reduce((sum, item) => sum + (Number(item.total_cost) || (Number(item.quantity) * Number(item.cost))), 0) || 0;
        
        const vatRate = updated.vat === 0 || updated.vat === 1 ? 0.07 : 0;
        const vatAmt = updated.vat === 1 ? subtotal * vatRate : updated.vat === 0 ? subtotal - (subtotal / 1.07) : 0;
        const grandTotal = updated.vat === 1 ? subtotal + vatAmt : subtotal;

        const completeData: purchaseOrder = {
            ...updated,
            quantity: qty,
            weight: weight,
            cost: subtotal,
            vat_amount: vatAmt,
            total_cost: grandTotal
        };

        setData(completeData);
        props.onChange(completeData);
    };

    const onVendorChange = (vendorId: number) => {
        const foundVendor = vendorList.find(v => v.id === vendorId);
        const updated: purchaseOrder = {
            ...data,
            vendor: vendorId,
            vendor_name: foundVendor?.name || '',
            vendor_tel: foundVendor?.tel || '',
            vendor_address: foundVendor?.address || ''
        };
        updateAndNotify(updated);
    };

    // ⚡ Auto-Fill Low Stock Products for Selected Vendor
    const handleAutoFillLowStock = () => {
        if (lowStockProducts.length === 0) {
            message.info('ไม่พบสินค้าใกล้หมดสำหรับผู้ขายส่งรายนี้');
            return;
        }

        let currentDetails = [...(data.detail || [])];
        let addedCount = 0;
        let cappedCount = 0;
        const skipped: string[] = [];
        const blank: string[] = [];

        lowStockProducts.forEach(prod => {
            const existingIndex = currentDetails.findIndex(d => d.product === prod.id);
            // Consigned goods on the shelf take up the same room under the
            // cap as the shop's own: 10 consigned + 20 ordered against a cap
            // of 25 would land 30.
            const inStock = poSellableQuantity(prod);

            // "จำนวนสั่งซื้อเริ่มต้น" is what the shop wants ordered. When it
            // is not set the system does NOT guess (it used to: twice the
            // order point minus stock, which ran away on a high order
            // point). The line goes in with a blank quantity, at the top,
            // and the sheet cannot be saved until someone types one.
            const hasWanted = Number(prod.init_order_amount) > 0;
            const wanted = hasWanted ? Number(prod.init_order_amount) : 0;

            // "จำกัดจำนวน" is a ceiling on what the shop should HOLD, not on
            // what it orders -- so the room left is the cap minus what is
            // already on the shelf. Ordering the full 20 against a cap of 25
            // with 8 in stock would land 28.
            const cap = prod.limit_quantity;
            const hasCap = cap !== null && cap !== undefined && (cap as any) !== '';
            const room = hasCap ? Number(cap) - inStock : Infinity;

            if (room <= 0) {
                // Already at or over the ceiling. A line of zero is noise,
                // so the product is skipped and named -- reaching here also
                // means the order point sits above the cap, which is a
                // configuration the shop will want to know about.
                skipped.push(prod.product_name);
                return;
            }

            const suggestedQty = Math.min(wanted, room);
            if (hasWanted && suggestedQty < wanted) cappedCount++;

            const unitCost = Number(prod.product_cost) || Number(prod.price) || 0;

            if (!hasWanted) {
                // Already on the sheet with a number: leave it alone.
                if (existingIndex >= 0) return;
                currentDetails = insertBlankLine(currentDetails, {
                    key: 0,
                    id: 0,
                    product: prod.id,
                    product_code: prod.product_code,
                    product_name: prod.product_name,
                    product_unit_name: prod.product_unit_name || 'ชิ้น',
                    quantity: 0,
                    current_stock: poSellableQuantity(prod),
                    point_purchase: prod.point_purchase ?? null,
                    weight: Number(prod.weight) || 0,
                    cost: unitCost,
                    total_cost: 0,
                    vat: prod.vat || -1,
                    remark: 'ดึงจากสินค้าใกล้หมด',
                    status: 1
                });
                addedCount++;
                blank.push(prod.product_name);
                return;
            }

            if (existingIndex >= 0) {
                // Raise an existing line, but never past the ceiling.
                currentDetails[existingIndex].quantity = Math.min(
                    Math.max(currentDetails[existingIndex].quantity, suggestedQty),
                    hasCap ? Number(cap) - inStock : Infinity
                );
                currentDetails[existingIndex].total_cost = currentDetails[existingIndex].quantity * currentDetails[existingIndex].cost;
            } else {
                currentDetails.push({
                    key: currentDetails.length + 1,
                    id: 0,
                    product: prod.id,
                    product_code: prod.product_code,
                    product_name: prod.product_name,
                    product_unit_name: prod.product_unit_name || 'ชิ้น',
                    quantity: suggestedQty,
                    current_stock: poSellableQuantity(prod),
                    point_purchase: prod.point_purchase ?? null,
                    weight: Number(prod.weight) || 0,
                    cost: unitCost,
                    total_cost: suggestedQty * unitCost,
                    vat: prod.vat || -1,
                    remark: 'ดึงจากสินค้าใกล้หมด',
                    status: 1
                });
                addedCount++;
            }
        });

        const updated: purchaseOrder = {
            ...data,
            detail: currentDetails
        };
        updateAndNotify(updated);
        message.success(
            blank.length === 0
                ? `⚡ ดึงสินค้าใกล้หมดของ ${data.vendor_name} สำเร็จ เพิ่ม ${addedCount} รายการ พร้อมคำนวณจำนวนที่ควรสั่งซื้อเรียบร้อยแล้ว! 🎉`
                : `⚡ ดึงสินค้าใกล้หมดของ ${data.vendor_name} สำเร็จ เพิ่ม ${addedCount} รายการ`
        );
        if (blank.length > 0) {
            message.warning(
                `${blank.length} รายการยังไม่ได้ใส่จำนวน เพราะไม่ได้ตั้ง "จำนวนสั่งซื้อเริ่มต้น" — อยู่บนสุดของตาราง กรุณาพิมพ์จำนวนเอง: ` +
                describeBlankLines(currentDetails.filter(d => blank.includes(d.product_name))),
                6
            );
        }
        if (cappedCount > 0) {
            message.info(`ลดจำนวนสั่งซื้อ ${cappedCount} รายการ ให้ไม่เกิน "จำกัดจำนวน" ที่ตั้งไว้`);
        }
        if (skipped.length > 0) {
            message.warning(
                `ข้าม ${skipped.length} รายการ เพราะมีของถึงจำกัดจำนวนแล้ว: ${skipped.slice(0, 3).join(', ')}` +
                (skipped.length > 3 ? ` และอีก ${skipped.length - 3} รายการ` : '')
            );
        }
    };

    // Add Product from Barcode Input
    const handleBarcodeKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && barcodeInput.trim()) {
            const code = barcodeInput.trim().toUpperCase();
            try {
                const found = await api_product_lookup_code(code);
                if (found && (found.id || found.product_code)) {
                    addProductToDetail(found);
                    setBarcodeInput('');
                } else {
                    const localFound = allProducts.find(p => p.product_code.toUpperCase() === code || p.product_name.includes(code));
                    if (localFound) {
                        addProductToDetail(localFound);
                        setBarcodeInput('');
                    } else {
                        message.warning(`ไม่พบสินค้ารหัส: ${code}`);
                    }
                }
            } catch (err) {
                const localFound = allProducts.find(p => p.product_code.toUpperCase() === code || p.product_name.includes(code));
                if (localFound) {
                    addProductToDetail(localFound);
                    setBarcodeInput('');
                } else {
                    message.error('ค้นหาสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ');
                }
            }
        }
    };

    // Add product object to detail table.
    //
    // `useDefaultQuantity` is set only by the product picker (เลือกสินค้า
    // จากรายการ -> + เลือก): picking a product from the list is choosing
    // WHAT to order, so the new line starts at the shop's configured
    // "จำนวนสั่งซื้อเริ่มต้น". A barcode scan stays one scan = one unit, as
    // it always has. Either way the quantity stays editable, and picking a
    // product that is already on the order still adds just one -- the
    // default is never stacked on top of an existing line.
    const addProductToDetail = (prod: product, useDefaultQuantity = false) => {
        const currentDetails = [...(data.detail || [])];
        const existingIndex = currentDetails.findIndex(d => d.product === prod.id);
        const unitCost = Number(prod.product_cost) || Number(prod.price) || 0;
        const configured = Number(prod.init_order_amount);
        const startQuantity = useDefaultQuantity && Number.isFinite(configured) && configured > 0
            ? configured
            : 1;

        if (existingIndex >= 0) {
            currentDetails[existingIndex].quantity += 1;
            currentDetails[existingIndex].total_cost = currentDetails[existingIndex].quantity * currentDetails[existingIndex].cost;
        } else {
            currentDetails.push({
                key: currentDetails.length + 1,
                id: 0,
                product: prod.id,
                product_code: prod.product_code,
                product_name: prod.product_name,
                product_unit_name: prod.product_unit_name || 'ชิ้น',
                quantity: startQuantity,
                current_stock: poSellableQuantity(prod),
                point_purchase: prod.point_purchase ?? null,
                weight: Number(prod.weight) || 0,
                cost: unitCost,
                total_cost: startQuantity * unitCost,
                vat: prod.vat || -1,
                remark: '',
                status: 1
            });
        }

        updateAndNotify({ ...data, detail: currentDetails });
        message.success(`เพิ่ม "${prod.product_name}" ในใบสั่งซื้อเรียบร้อยแล้ว`);
    };

    const handleQuantityChange = (index: number, newQty: number) => {
        const currentDetails = [...(data.detail || [])];
        const line = currentDetails[index];
        const typed = Number(newQty);
        if (line.needs_quantity && !(typed >= 1)) {
            // Cleared again: stays blank, stays flagged.
            currentDetails[index] = { ...line, quantity: 0, total_cost: 0 };
            updateAndNotify({ ...data, detail: currentDetails });
            return;
        }
        const qty = Math.max(1, typed || 1);
        currentDetails[index] = { ...line, quantity: qty, total_cost: qty * line.cost };
        updateAndNotify({ ...data, detail: currentDetails });
    };

    const handleCostChange = (index: number, newCost: number) => {
        const currentDetails = [...(data.detail || [])];
        const cost = Math.max(0, Number(newCost) || 0);
        currentDetails[index].cost = cost;
        currentDetails[index].total_cost = currentDetails[index].quantity * cost;
        updateAndNotify({ ...data, detail: currentDetails });
    };

    const handleDeleteItem = (index: number) => {
        const currentDetails = data.detail?.filter((_, idx) => idx !== index) || [];
        updateAndNotify({ ...data, detail: currentDetails });
    };

    const handleSendToVendor = () => {
        if (!data.vendor || Number(data.vendor) <= 0) {
            Modal.warning({
                title: 'กรุณาเลือกผู้ขายส่ง',
                content: 'กรุณาเลือกผู้จัดจำหน่าย / ผู้ขายส่ง (Vendor) ก่อนส่งคำสั่งซื้อ',
                okText: 'ตกลง',
                centered: true
            });
            return;
        }

        if (!data.detail || data.detail.length === 0) {
            Modal.warning({
                title: 'ไม่มีรายการสินค้า',
                content: 'กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการในใบสั่งซื้อ',
                okText: 'ตกลง',
                centered: true
            });
            return;
        }

        const blankLines = linesNeedingQuantity(data.detail);
        if (blankLines.length > 0) {
            Modal.warning({
                title: 'มีสินค้าที่ยังไม่ได้ใส่จำนวน',
                content: `กรุณาพิมพ์จำนวนสั่งซื้อให้ครบก่อนส่ง (${blankLines.length} รายการ อยู่บนสุดของตาราง): ${describeBlankLines(blankLines)}`,
                okText: 'ตกลง',
                centered: true
            });
            return;
        }

        Modal.confirm({
            title: 'ยืนยันส่งคำสั่งซื้อให้ Vendor',
            content: `คุณต้องการยืนยันการส่งคำสั่งซื้อ ${data.document_no || ''} ให้กับ ${data.vendor_name || 'ผู้ขายส่ง'} ใช่หรือไม่? สถานะเอกสารจะเปลี่ยนเป็น "ส่งคำสั่งซื้อแล้ว / รอรับสินค้า"`,
            okText: 'ยืนยันส่งคำสั่งซื้อ',
            cancelText: 'ยกเลิก',
            centered: true,
            async onOk() {
                try {
                    const updatedData: purchaseOrder = { ...data, status: 2 };
                    let operation = updatedData.id && updatedData.id > 0 ? 'UPDATE' : 'INSERT';
                    let key_id = updatedData.id && updatedData.id > 0 ? updatedData.id : undefined;

                    const res = await api_purchase_order(operation, updatedData, key_id);
                    // Sending to the vendor moves the document to "รอรับสินค้า".
                    // `|| res` let the wrapper's [] through as success.
                    if (isConfirmedWrite(res)) {
                        let savedPO = updatedData;
                        if (operation === 'INSERT') {
                            try {
                                const listRes = await api_purchase_order('LIST', {});
                                if (Array.isArray(listRes) && listRes.length > 0) {
                                    const latest = listRes[0];
                                    if (latest && latest.id) {
                                        savedPO = { ...savedPO, id: latest.id, document_no: latest.document_no, key: latest.id };
                                    }
                                }
                            } catch (e) {}
                        }
                        setData(savedPO);
                        props.onChange(savedPO);
                        Modal.success({
                            title: 'ส่งคำสั่งซื้อสำเร็จ',
                            content: 'เปลี่ยนสถานะเป็น "ส่งคำสั่งซื้อแล้ว / รอรับสินค้า" เรียบร้อยแล้ว พร้อมสำหรับสร้างใบรับเข้าสินค้า',
                            okText: 'ตกลง',
                            centered: true,
                            onOk() {
                                if (props.onReload) props.onReload();
                            }
                        });
                    } else {
                        Modal.error({
                            title: 'ผิดพลาด',
                            content: 'ไม่สามารถส่งคำสั่งซื้อได้ กรุณาลองใหม่อีกครั้ง',
                            okText: 'ตกลง',
                            centered: true
                        });
                    }
                } catch (err: any) {
                    console.error('Send to vendor error:', err);
                    Modal.error({
                        title: 'ผิดพลาด',
                        // `err.message` on an axios failure is "Request failed
                        // with status code 400" -- Backend's own wording is
                        // the useful one, now that the client passes it on.
                        content: backendMessage(err, 'ไม่สามารถส่งคำสั่งซื้อได้ กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง'),
                        okText: 'ตกลง',
                        centered: true
                    });
                }
            }
        });
    };

    const handleCancelPurchaseOrder = () => {
        if (!data.id || data.id <= 0) return;

        Modal.confirm({
            title: 'ยืนยันการยกเลิกใบสั่งซื้อ',
            content: `คุณต้องการยกเลิกใบสั่งซื้อ ${data.document_no || ''} ใช่หรือไม่? การดำเนินการนี้จะเปลี่ยนสถานะเอกสารเป็น "ยกเลิก"`,
            okText: 'ยกเลิกใบสั่งซื้อ',
            okType: 'danger',
            cancelText: 'ปิด',
            centered: true,
            async onOk() {
                try {
                    const res = await api_purchase_order('DELETE', {}, data.id);
                    if (isConfirmedWrite(res)) {
                        const updated: purchaseOrder = { ...data, status: 0 };
                        setData(updated);
                        props.onChange(updated);
                        Modal.success({
                            title: 'ยกเลิกสำเร็จ',
                            content: 'ยกเลิกใบสั่งซื้อเรียบร้อยแล้ว',
                            okText: 'ตกลง',
                            centered: true,
                            onOk() {
                                if (props.onReload) props.onReload();
                            }
                        });
                    } else {
                        Modal.error({
                            title: 'ผิดพลาด',
                            content: 'ไม่สามารถยกเลิกใบสั่งซื้อได้',
                            okText: 'ตกลง',
                            centered: true
                        });
                    }
                } catch (err: any) {
                    console.error('Cancel PO error:', err);
                    const msg = err?.response?.data?.message || err?.message || 'ไม่สามารถยกเลิกใบสั่งซื้อได้';
                    Modal.error({
                        title: 'ไม่สามารถยกเลิกได้',
                        content: msg,
                        okText: 'ตกลง',
                        centered: true
                    });
                }
            }
        });
    };

    const handleOpenGoodReceiveDraft = () => {
        navigate('/good/receive');
        message.info(`เปิดหน้ารับเข้าสินค้าเพื่อตรวจนับและยืนยัน ${data.active_draft_doc_no || 'ฉบับร่าง'}`);
    };

    const handleCreateGoodReceiveDraft = async () => {
        if (!data.id || data.id <= 0) return;

        if (data.status !== 2) {
            Modal.warning({
                title: 'ไม่สามารถสร้างใบรับเข้าได้',
                content: 'ใบสั่งซื้อต้องอยู่ในสถานะ "รอรับสินค้า / ส่งสั่งซื้อแล้ว" เท่านั้น',
                okText: 'เข้าใจแล้ว',
                centered: true
            });
            return;
        }

        Modal.confirm({
            title: 'ยืนยันการสร้างใบรับเข้าสินค้า (ฉบับร่าง)',
            content: `คุณต้องการสร้างใบรับเข้าฉบับร่างจากใบสั่งซื้อ ${data.document_no || 'PO'} สำหรับสินค้าที่ยังไม่ได้รับเข้าหรือไม่?`,
            okText: 'สร้างใบรับเข้าฉบับร่าง',
            cancelText: 'ยกเลิก',
            centered: true,
            async onOk() {
                try {
                    const res = await api_good_receive('CREATE_DRAFT_FROM_PO', { purchase_order: data.id });
                    if (res && (res.document_no || res.id)) {
                        const draftDoc = res.document_no || `ID: ${res.id}`;
                        const updated = {
                            ...data,
                            active_draft_gr_id: res.id,
                            active_draft_doc_no: res.document_no || draftDoc
                        };
                        setData(updated);
                        props.onChange(updated);
                        Modal.success({
                            title: 'สร้างใบรับเข้าฉบับร่างสำเร็จ',
                            content: `สร้างใบรับเข้าฉบับร่างเลขที่ ${draftDoc} เรียบร้อยแล้ว (สามารถตรวจนับและยืนยันการรับเข้าสินค้าได้ที่หน้ารับเข้าสินค้า)`,
                            okText: 'ตกลง',
                            centered: true
                        });
                        if (props.onReload) {
                            props.onReload();
                        }
                    } else {
                        Modal.error({
                            title: 'ไม่สามารถสร้างใบรับเข้าฉบับร่างได้',
                            content: 'ระบบไม่สามารถสร้างใบรับเข้าฉบับร่างได้ กรุณาลองใหม่อีกครั้ง',
                            okText: 'ตกลง',
                            centered: true
                        });
                    }
                } catch (err: any) {
                    console.error('Create GR draft error:', err);
                    const errMsg = err?.response?.data?.message || err?.message || 'ไม่สามารถสร้างใบรับเข้าฉบับร่างได้';
                    const match = errMsg.match(/GR\d+/i);
                    if (match) {
                        const existingDoc = match[0];
                        const updated = {
                            ...data,
                            active_draft_doc_no: existingDoc
                        };
                        setData(updated);
                        props.onChange(updated);
                    }
                    Modal.warning({
                        title: 'พบใบรับเข้าสินค้าฉบับร่างอยู่แล้ว',
                        content: errMsg,
                        okText: 'ตกลง',
                        centered: true
                    });
                }
            }
        });
    };

    const itemColumns = [
        {
            title: 'ลำดับ',
            dataIndex: 'key',
            key: 'key',
            width: '60px',
            align: 'center' as const,
            render: (_: any, __: any, index: number) => index + 1
        },
        {
            title: 'รหัสสินค้า',
            dataIndex: 'product_code',
            key: 'product_code',
            width: '140px',
            render: (code: string) => <Tag color="blue" style={{ fontWeight: 600 }}>{code}</Tag>
        },
        {
            title: 'ชื่อสินค้า',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (name: string, record: purchaseOrderDetail) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                        คงเหลือ: <span style={{ color: record.point_purchase != null && Number(record.current_stock) <= Number(record.point_purchase) ? '#EF4444' : '#10B981', fontWeight: 700 }}>{record.current_stock || 0}</span> {record.product_unit_name || 'ชิ้น'} (จุดสั่งซื้อ: {record.point_purchase ?? 'ไม่ได้ตั้ง'})
                    </div>
                </div>
            )
        },
        {
            title: 'จำนวนที่สั่ง',
            dataIndex: 'quantity',
            key: 'quantity',
            width: '130px',
            align: 'center' as const,
            render: (qty: number, record: purchaseOrderDetail, index: number) => (
                <Input
                    type="number"
                    min={1}
                    data-testid={`po-qty-${record.product}`}
                    value={record.needs_quantity && !(qty >= 1) ? '' : qty}
                    placeholder={record.needs_quantity ? 'พิมพ์จำนวน' : undefined}
                    status={record.needs_quantity && !(qty >= 1) ? 'warning' : undefined}
                    disabled={props.disabled}
                    onChange={(e) => handleQuantityChange(index, parseInt(e.target.value))}
                    style={{ textAlign: 'center', fontWeight: 700, borderRadius: '8px' }}
                />
            )
        },
        {
            title: 'ต้นทุน/หน่วย (฿)',
            dataIndex: 'cost',
            key: 'cost',
            width: '140px',
            align: 'right' as const,
            render: (cost: number, record: purchaseOrderDetail, index: number) => (
                <Input
                    type="number"
                    min={0}
                    step={0.5}
                    value={cost}
                    disabled={props.disabled}
                    onChange={(e) => handleCostChange(index, parseFloat(e.target.value))}
                    style={{ textAlign: 'right', fontWeight: 600, borderRadius: '8px' }}
                />
            )
        },
        {
            title: 'ราคารวม (฿)',
            dataIndex: 'total_cost',
            key: 'total_cost',
            width: '130px',
            align: 'right' as const,
            render: (total: number, record: purchaseOrderDetail) => (
                <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>
                    {Number(total || (record.quantity * record.cost) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
            )
        },
        {
            title: 'การดำเนินการ',
            key: 'action',
            width: '80px',
            align: 'center' as const,
            render: (_: any, record: purchaseOrderDetail, index: number) => !props.disabled ? (
                <button
                    type="button"
                    className="table-action-button delete"
                    title="ลบรายการ"
                    onClick={() => handleDeleteItem(index)}
                >
                    <FontAwesomeIcon icon="trash-can" />
                </button>
            ) : null
        }
    ];

    const [modalProducts, setModalProducts] = useState<product[]>([]);
    const [isModalLoading, setIsModalLoading] = useState(false);

    // TRUE server-side pagination: the backend returns at most `modalPageSize` rows.
    useEffect(() => {
        if (!isProductModalOpen) return;
        const timer = setTimeout(async () => {
            setIsModalLoading(true);
            try {
                const res = await api_product('LIST', {
                    search: productSearchKeyword.trim(),
                    page: modalPage,
                    page_size: modalPageSize
                });
                const items = Array.isArray(res) ? res : (res?.items || []);
                setModalProducts(items);
                setModalTotal(Array.isArray(res) ? items.length : (res?.pagination?.total || 0));
            } catch (e) {
                console.error(e);
            } finally {
                setIsModalLoading(false);
            }
        }, 200);
        return () => clearTimeout(timer);
    }, [isProductModalOpen, productSearchKeyword, modalPage, modalPageSize]);

    // A new keyword always restarts from page 1.
    useEffect(() => {
        setModalPage(1);
    }, [productSearchKeyword, isProductModalOpen]);

    const filteredModalProducts = modalProducts.length > 0 || productSearchKeyword
        ? modalProducts
        : allProducts;

// Simple, clean Dark-Mode compatible PO Workflow Stepper
const PoWorkflowStepper: React.FC<{ status: number }> = ({ status }) => {
    const steps = [
        { key: 1, label: '1. ฉบับร่าง', sub: 'Draft', color: '#D97706', activeBg: '#FEF3C7', activeBorder: '#F59E0B' },
        { key: 2, label: '2. ส่งคำสั่งซื้อแล้ว', sub: 'Sent / รอรับสินค้า', color: '#2563EB', activeBg: '#DBEAFE', activeBorder: '#3B82F6' },
        { key: 3, label: '3. รับสินค้าครบแล้ว', sub: 'Fully Received', color: '#059669', activeBg: '#D1FAE5', activeBorder: '#10B981' }
    ];

    if (status === 0) {
        return (
            <div style={{
                padding: '10px 16px',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '10px',
                color: '#DC2626',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px'
            }} data-testid="po-workflow-stepper">
                <span style={{ fontSize: '16px' }}>🔴</span>
                <span>สถานะเอกสาร: <strong>ยกเลิก (Cancelled)</strong></span>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: '#F8FAFC',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '8px'
        }} data-testid="po-workflow-stepper">
            {steps.map((s, idx) => {
                const isActive = status === s.key;
                const isPassed = status > s.key;
                return (
                    <React.Fragment key={s.key}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            background: isActive ? s.activeBg : isPassed ? '#E2E8F0' : '#FFFFFF',
                            color: isActive ? s.color : isPassed ? '#334155' : '#94A3B8',
                            border: isActive ? `1.5px solid ${s.activeBorder}` : '1px solid #E2E8F0',
                            fontWeight: isActive ? 700 : 500,
                            fontSize: '13px',
                            transition: 'all 0.2s ease'
                        }}>
                            <span>{isActive ? '👉' : isPassed ? '✅' : '⚪'}</span>
                            <div>
                                <div style={{ color: isActive ? s.color : undefined }}>{s.label}</div>
                                <div style={{ fontSize: '10px', opacity: 0.85 }}>{s.sub}</div>
                            </div>
                        </div>
                        {idx < steps.length - 1 && (
                            <span style={{ color: isPassed ? '#3B82F6' : '#CBD5E1', fontWeight: 700, fontSize: '14px' }}>➔</span>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

    return (
        <div className="po-detail-container" data-testid="purchase-order-detail-container">
            {/* Visual Workflow Stepper */}
            <PoWorkflowStepper status={data.status || 1} />

            {/* Header Form */}
            <div className="po-form-grid">
                {data.id && data.id > 0 && data.document_no ? (
                    <div className="formGroup">
                        <label className="formLabel">เลขที่ใบสั่งซื้อ (PO No.)</label>
                        <div className="po-doc-no-display" style={{ fontWeight: 700, fontSize: '15px', padding: '6px 0' }} data-testid="display-po-doc-no">
                            {data.document_no}
                        </div>
                    </div>
                ) : null}

                <div className="formGroup">
                    <label className="formLabel required">ผู้จัดจำหน่าย / ผู้ขายส่ง (Vendor)</label>
                    <Select
                        data-testid="select-vendor"
                        showSearch
                        placeholder="เลือกผู้ขายส่ง..."
                        value={data.vendor || undefined}
                        disabled={props.disabled || data.status === 3 || data.status === 0}
                        onChange={onVendorChange}
                        filterOption={(input, option) =>
                            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                    >
                        {vendorList.map(v => (
                            <Option key={v.id} value={v.id}>{v.name}</Option>
                        ))}
                    </Select>
                </div>

                <div className="formGroup">
                    <label className="formLabel">สถานะเอกสาร</label>
                    <div style={{ padding: '6px 0' }} data-testid="po-status-badge">
                        {(data.status === 1 || !data.status) && (
                            <Tag color="warning" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700, borderRadius: '6px' }}>
                                🟡 ฉบับร่าง (Draft)
                            </Tag>
                        )}
                        {data.status === 2 && (
                            <Tag color="processing" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700, borderRadius: '6px' }}>
                                🔵 ส่งคำสั่งซื้อแล้ว / รอรับสินค้า
                            </Tag>
                        )}
                        {data.status === 3 && (
                            <Tag color="success" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700, borderRadius: '6px' }}>
                                🟢 รับสินค้าครบแล้ว (System Controlled)
                            </Tag>
                        )}
                        {data.status === 0 && (
                            <Tag color="error" style={{ fontSize: '13px', padding: '4px 10px', fontWeight: 700, borderRadius: '6px' }}>
                                🔴 ยกเลิก (Cancelled)
                            </Tag>
                        )}
                    </div>
                </div>
            </div>

            {/* ⚡ Smart Low-Stock Vendor Action Bar */}
            {data.vendor > 0 && (
                <div className="po-vendor-smart-bar" data-testid="po-vendor-smart-bar">
                    <div className="po-vendor-info">
                        <span>🏢 <strong>ผู้ขายส่ง:</strong> {data.vendor_name}</span>
                        {lowStockProducts.length > 0 ? (
                            <span className="badge-low-stock-alert">
                                ⚠️ พบสินค้าใกล้หมด {lowStockProducts.length} รายการ
                            </span>
                        ) : (
                            <span className="badge-stock-ok">
                                ✅ สินค้าของ Vendor นี้สต๊อกเพียงพอ
                            </span>
                        )}
                    </div>

                    <Button
                        type="primary"
                        className="btn-auto-fill-low-stock"
                        onClick={handleAutoFillLowStock}
                        disabled={props.disabled || lowStockProducts.length === 0}
                        data-testid="btn-auto-fill-low-stock"
                    >
                        <FontAwesomeIcon icon="bolt" />
                        <span>ดึงสินค้าใกล้หมดของ Vendor นี้ ({lowStockProducts.length})</span>
                    </Button>
                </div>
            )}

            {/* Product Quick-Add Bar */}
            {!props.disabled && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <Input
                            ref={barcodeInputRef}
                            prefix={<FontAwesomeIcon icon="magnifying-glass" style={{ color: '#94A3B8' }} />}
                            placeholder="สแกนบาร์โค้ด หรือพิมพ์รหัสสินค้า แล้วกด Enter..."
                            value={barcodeInput}
                            onChange={(e) => setBarcodeInput(e.target.value)}
                            onKeyDown={handleBarcodeKeyDown}
                            style={{ borderRadius: '10px' }}
                        />
                    </div>
                    <Button
                        type="default"
                        icon={<FontAwesomeIcon icon="plus" />}
                        onClick={() => setIsProductModalOpen(true)}
                        style={{ borderRadius: '10px', fontWeight: 600 }}
                    >
                        เลือกสินค้าจากรายการ
                    </Button>
                </div>
            )}

            {/* Lines pulled in without a default order quantity sit at the top
                and are named here until every one of them has a number. */}
            {linesNeedingQuantity(data.detail).length > 0 && (
                <div data-testid="po-needs-quantity-alert">
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: '10px', borderRadius: '8px' }}
                        message={`${linesNeedingQuantity(data.detail).length} รายการยังไม่ได้ใส่จำนวนสั่งซื้อ (อยู่บนสุดของตาราง): ${describeBlankLines(linesNeedingQuantity(data.detail))}`}
                        description={'สินค้าเหล่านี้ไม่ได้ตั้ง "จำนวนสั่งซื้อเริ่มต้น" ระบบจึงไม่เดาจำนวนให้ กรุณาพิมพ์จำนวนเองก่อนบันทึกหรือส่งคำสั่งซื้อ'}
                    />
                </div>
            )}

            {/* Items Table */}
            <Table
                columns={itemColumns}
                dataSource={data.detail || []}
                rowClassName={(record: purchaseOrderDetail) => (needsQuantity(record) ? 'po-line-needs-quantity' : '')}
                pagination={false}
                size="middle"
                bordered
                locale={{ emptyText: 'ยังไม่มีรายการสินค้าในใบสั่งซื้อ (เลือกผู้ขายส่งด้านบนเพื่อดึงสินค้าใกล้หมด หรือสแกนบาร์โค้ด)' }}
            />

            {/* Remarks & Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
                <div className="formGroup">
                    <label className="formLabel">หมายเหตุ / คำสั่งเพิ่มเติม</label>
                    <TextArea
                        rows={3}
                        placeholder="ระบุข้อความหรือเงื่อนไขถึงผู้ขายส่ง เช่น กำหนดวันส่งของ, สถานที่ส่ง..."
                        value={data.remark || ''}
                        disabled={props.disabled}
                        onChange={(e) => updateAndNotify({ ...data, remark: e.target.value })}
                    />
                </div>

                <div className="po-summary-box">
                    <div className="po-summary-list">
                        <div className="po-summary-row">
                            <span>จำนวนรายการ:</span>
                            <strong>{data.detail?.length || 0} รายการ</strong>
                        </div>
                        <div className="po-summary-row">
                            <span>จำนวนชิ้นรวม:</span>
                            <strong>{data.quantity || 0} ชิ้น</strong>
                        </div>
                        <div className="po-summary-row">
                            <span>ยอดรวมก่อนภาษี (Subtotal):</span>
                            <strong>{Number(data.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</strong>
                        </div>
                        <div className="po-summary-row" style={{ alignItems: 'center' }}>
                            <span>ภาษีมูลค่าเพิ่ม:</span>
                            <Select
                                value={data.vat !== undefined ? data.vat : -1}
                                disabled={props.disabled}
                                onChange={(val) => updateAndNotify({ ...data, vat: val })}
                                style={{ width: '160px' }}
                            >
                                <Option value={-1}>❌ ไม่คิด VAT</Option>
                                <Option value={0}>รวมใน 7% (Inclusive)</Option>
                                <Option value={1}>แยกนอก 7% (Exclusive)</Option>
                            </Select>
                        </div>
                        {data.vat !== -1 && (
                            <div className="po-summary-row">
                                <span>มูลค่าภาษี VAT 7%:</span>
                                <strong>{Number(data.vat_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</strong>
                            </div>
                        )}
                        <div className="po-summary-row grand-total">
                            <span>ยอดสั่งซื้อสุทธิ:</span>
                            <span>{Number(data.total_cost || data.cost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} ฿</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Actions for Workflow, Print and Good Receive conversion */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '16px', borderTop: '1.5px solid #F1F5F9', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Button
                        type="default"
                        icon={<FontAwesomeIcon icon="print" />}
                        onClick={() => setIsPrintModalOpen(true)}
                        style={{ borderRadius: '8px', fontWeight: 600 }}
                    >
                        🖨️ พิมพ์ใบสั่งซื้อ (A4)
                    </Button>

                    {data.id && data.id > 0 && (data.status === 1 || data.status === 2) && !props.disabled && (
                        <Button
                            danger
                            onClick={handleCancelPurchaseOrder}
                            style={{ borderRadius: '8px', fontWeight: 600 }}
                            data-testid="btn-cancel-po"
                        >
                            🗑️ ยกเลิกใบสั่งซื้อ
                        </Button>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {/* Status = 1 (Draft): Primary action is "ส่งคำสั่งซื้อให้ Vendor" */}
                    {(data.status === 1 || !data.status) && !props.disabled && (
                        <Button
                            type="primary"
                            onClick={handleSendToVendor}
                            style={{ background: '#2563EB', borderColor: '#2563EB', borderRadius: '8px', fontWeight: 700 }}
                            data-testid="btn-send-to-vendor"
                        >
                            📤 ส่งคำสั่งซื้อให้ Vendor
                        </Button>
                    )}

                    {/* Status = 2 (Waiting Goods Receive): Primary action is "สร้างใบรับเข้าสินค้าฉบับร่าง" or "เปิดใบรับเข้าสินค้าฉบับร่าง" */}
                    {data.id && data.id > 0 && data.status === 2 && (
                        data.active_draft_doc_no ? (
                            <Button
                                type="primary"
                                style={{ background: '#0284C7', borderColor: '#0284C7', borderRadius: '8px', fontWeight: 700 }}
                                onClick={handleOpenGoodReceiveDraft}
                                data-testid="btn-open-gr-draft"
                            >
                                📂 เปิดใบรับเข้าสินค้าฉบับร่าง {data.active_draft_doc_no}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                style={{ background: '#10B981', borderColor: '#10B981', borderRadius: '8px', fontWeight: 700 }}
                                onClick={handleCreateGoodReceiveDraft}
                                data-testid="btn-create-gr-draft"
                            >
                                📦 สร้างใบรับเข้าสินค้าฉบับร่าง
                            </Button>
                        )
                    )}

                    {/* Status = 3 (Fully Received): System controlled message */}
                    {data.status === 3 && (
                        <span style={{ color: '#059669', fontWeight: 600, fontSize: '13px' }}>
                            🟢 รับเข้าสินค้าครบถ้วนตามรายการแล้ว (System Controlled)
                        </span>
                    )}

                    {/* Status = 0 (Cancelled): Notice */}
                    {data.status === 0 && (
                        <span style={{ color: '#DC2626', fontWeight: 600, fontSize: '13px' }}>
                            🔴 เอกสารนี้ถูกยกเลิกแล้ว
                        </span>
                    )}
                </div>
            </div>

            {/* Modal: Select Products */}
            <Modal
                title="เลือกสินค้าเพื่อเพิ่มในใบสั่งซื้อ"
                open={isProductModalOpen}
                onCancel={() => setIsProductModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsProductModalOpen(false)}>ปิด</Button>
                ]}
                width={840}
                centered
            >
                <Input
                    prefix={<FontAwesomeIcon icon="magnifying-glass" style={{ color: '#94A3B8' }} />}
                    placeholder="ค้นหาตามรหัส หรือชื่อสินค้า..."
                    value={productSearchKeyword}
                    onChange={(e) => setProductSearchKeyword(e.target.value)}
                    style={{ marginBottom: '14px', borderRadius: '8px' }}
                />
                <Table
                    size="small"
                    loading={isModalLoading}
                    dataSource={filteredModalProducts}
                    rowKey="id"
                    scroll={{ y: 380 }}
                    pagination={{
                        current: modalPage,
                        pageSize: modalPageSize,
                        total: modalProducts.length > 0 || productSearchKeyword ? modalTotal : filteredModalProducts.length,
                        showSizeChanger: true,
                        pageSizeOptions: PRODUCT_PAGE_SIZE_OPTIONS.map(String),
                        selectComponentClass: PageSizeSelect,
                        locale: { items_per_page: '/ หน้า' },
                        onChange: (newPage, newPageSize) => {
                            // Changing page size must reset to page 1 and re-query the server.
                            if (newPageSize && newPageSize !== modalPageSize) {
                                setModalPageSize(newPageSize);
                                setModalPage(1);
                                return;
                            }
                            setModalPage(newPage);
                        },
                        showTotal: (total, range) => `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ`,
                        responsive: true,
                        size: 'small'
                    }}
                    columns={[
                        { title: 'รหัส', dataIndex: 'product_code', key: 'product_code', width: '120px' },
                        { title: 'ชื่อสินค้า', dataIndex: 'product_name', key: 'product_name' },
                        { 
                            title: 'สต๊อก', 
                            dataIndex: 'quantity', 
                            key: 'quantity',
                            width: '90px',
                            render: (_qty: number, rec: product) => (
                                <Tag color={rec.point_purchase != null && poSellableQuantity(rec) <= Number(rec.point_purchase) ? 'red' : 'green'}>
                                    {poSellableQuantity(rec)}
                                </Tag>
                            )
                        },
                        { 
                            title: 'ต้นทุน (฿)', 
                            dataIndex: 'product_cost', 
                            key: 'product_cost', 
                            width: '100px',
                            render: (cost: any, rec: product) => Number(cost || rec.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })
                        },
                        {
                            title: 'เลือก',
                            key: 'select',
                            width: '80px',
                            render: (_: any, prod: product) => (
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => {
                                        addProductToDetail(prod, true);
                                    }}
                                    style={{ borderRadius: '6px' }}
                                >
                                    + เลือก
                                </Button>
                            )
                        }
                    ]}
                />
            </Modal>

            {/* Modal: A4 Print Preview */}
            <Modal
                open={isPrintModalOpen}
                onCancel={() => setIsPrintModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsPrintModalOpen(false)} data-testid="btn-modal-close-print-po">ปิด</Button>,
                    <Button key="print" type="primary" onClick={handlePrintPurchaseOrder} data-testid="btn-modal-print-po" style={{ background: '#FF7A59', borderColor: '#FF7A59', fontWeight: 600 }}>🖨️ พิมพ์ออกเครื่องพิมพ์</Button>
                ]}
                width={840}
                centered
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                <PurchaseOrderPrint ref={printRef} data={data} onClose={() => setIsPrintModalOpen(false)} />
            </Modal>
        </div>
    );
};

export default PurchaseOrderDetail;
